import React from 'react';
import { FileText, Plus, Trash2, Search, User, Building, X } from 'lucide-react';

const DetailsSection = ({
  data,
  errors,
  onChange,
  onAddDetail,
  onRemoveDetail,
  onRemoveSupplier,
  onOpenModal,
  suppliersData,
  usersData
}) => {
  // Función para obtener el nombre del proveedor seleccionado
  const getSupplierName = (supplierId) => {
    if (!supplierId || !suppliersData?.suppliers) return 'Seleccionar proveedor';
    const supplier = suppliersData.suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.supplier_name : 'Proveedor no encontrado';
  };

  // Función para obtener el nombre del consignatario seleccionado
  const getConsigneeName = (consigneeId) => {
    if (!consigneeId || !usersData?.users) return 'Seleccionar consignatario';
    const consignee = usersData.users.find(u => u.id === consigneeId);
    return consignee ? consignee.full_name : 'Consignatario no encontrado';
  };

  // Función para obtener el nombre del FF seleccionado
  const getFFName = (ffId) => {
    if (!ffId || !usersData?.users) return 'Seleccionar FF (opcional)';
    const ff = usersData.users.find(u => u.id === ffId);
    return ff ? ff.full_name : 'FF no encontrado';
  };

  // Función para obtener el nombre del Vendedor seleccionado
  const getSellerName = (sellerId) => {
    if (!sellerId || !usersData?.users) return 'Sin vendedor';
    const seller = usersData.users.find(u => u.id === sellerId);
    return seller ? seller.full_name : 'Vendedor no encontrado';
  };

  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Detalles del Envío</h3>
        </div>
        <button
          type="button"
          onClick={onAddDetail}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Agregar Detalle</span>
        </button>
      </div>

      {/* Tabla de detalles */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                Conocimiento de Embarque *
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                Factura Comercial *
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                Consignatario *
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                Proveedores * (mínimo 1)
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                FF {data.some(d => d.isManualConsignee) && <span className="text-red-600">* si manual</span>}
                {!data.some(d => d.isManualConsignee) && <span className="text-gray-500">(Opcional)</span>}
              </th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">
                Vendedor <span className="text-gray-500">(Opcional)</span>
              </th>
              <th className="border border-gray-300 px-4 py-2 text-center text-sm font-medium text-gray-700">
                CIF
              </th>
              <th className="border border-gray-300 px-4 py-2 text-center text-sm font-medium text-gray-700">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((detail, index) => (
              <React.Fragment key={index}>
                {/* Fila principal del detalle */}
                <tr className="hover:bg-gray-50">
                  {/* Bill of Lading */}
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={detail.bill_of_lading}
                      onChange={(e) => onChange('bill_of_lading', e.target.value, index)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                      placeholder="BL-123456"
                    />
                  </td>

                  {/* Factura Comercial */}
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={detail.commercial_invoice}
                      onChange={(e) => onChange('commercial_invoice', e.target.value, index)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                      placeholder="INV-123456"
                    />
                  </td>

                  {/* Consignatario */}
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenModal('consignees', index)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500 text-left flex items-center justify-between"
                    >
                      <span className={detail.consigneeId ? 'text-gray-900' : (detail.isManualConsignee ? 'text-indigo-600 font-medium' : 'text-gray-500')}>
                        {detail.isManualConsignee ? 'Digitación manual' : getConsigneeName(detail.consigneeId)}
                      </span>
                      <Search className="w-3 h-3 text-gray-400" />
                    </button>
                  </td>

                  {/* Proveedores (Lista editable) */}
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="space-y-2">
                      {/* Lista de suppliers agregados */}
                      {detail.suppliers && detail.suppliers.length > 0 ? (
                        <div className="space-y-1 mb-2">
                          {detail.suppliers.map((supplier) => (
                            <div key={supplier.id} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-sm">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{supplier.supplier_name}</div>
                                {supplier.email && (
                                  <div className="text-xs text-gray-500">{supplier.email}</div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => onRemoveSupplier(index, supplier.id)}
                                className="ml-2 text-red-600 hover:text-red-800 focus:outline-none"
                                title="Eliminar proveedor"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic mb-2">Sin proveedores</div>
                      )}

                      {/* Botón para agregar supplier */}
                      <button
                        type="button"
                        onClick={() => onOpenModal('suppliers', index)}
                        className="w-full px-2 py-1 border border-dashed border-gray-400 rounded hover:border-indigo-500 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-left flex items-center justify-center text-sm text-gray-600 hover:text-indigo-600"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        <span>Agregar Proveedor</span>
                      </button>
                    </div>
                  </td>

                  {/* FF (Obligatorio si es consignatario manual o Transportista con additional_consignees) */}
                  <td className="border border-gray-300 px-4 py-2">
                    {(() => {
                      const requiresFF = detail.isManualConsignee || (detail.isTransportistaConsignee && detail.additional_consignees?.trim());
                      const showError = requiresFF && !detail.ff;

                      return (
                        <button
                          type="button"
                          onClick={() => onOpenModal('ff', index)}
                          className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 text-left flex items-center justify-between ${
                            showError
                              ? 'border-red-500 bg-red-50 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-gray-500'
                          }`}
                          title={showError
                            ? "FF es OBLIGATORIO para consignatarios manuales o Transportistas con consignatarios adicionales"
                            : requiresFF
                              ? "FF obligatorio (consignatarios manuales o Transportista con additional_consignees)"
                              : "FF opcional"
                          }
                        >
                          <span className={detail.ff ? 'text-gray-900' : (requiresFF ? 'text-red-600 font-medium' : 'text-gray-500')}>
                            {detail.ff ? getFFName(detail.ff) : (requiresFF ? 'FF REQUERIDO' : 'Seleccionar FF (opcional)')}
                          </span>
                          <Search className={showError ? "w-3 h-3 text-red-600" : "w-3 h-3 text-gray-400"} />
                        </button>
                      );
                    })()}
                  </td>

                  {/* Vendedor (Opcional) */}
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenModal('seller', index)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500 text-left flex items-center justify-between"
                      title="Vendedor opcional - Puede dejarse vacío"
                    >
                      <span className={detail.seller ? 'text-gray-900' : 'text-gray-500'}>
                        {getSellerName(detail.seller)}
                      </span>
                      <Search className="w-3 h-3 text-gray-400" />
                    </button>
                  </td>

                  {/* CIF */}
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={detail.cif || false}
                      disabled={!detail.ff || detail.isManualConsignee} // 🔒 Deshabilitado si FF es null O si es consignatario manual
                      onChange={(e) => onChange('cif', e.target.checked, index)}
                      className={`w-4 h-4 rounded focus:ring-gray-500 focus:ring-2 ${
                        detail.ff && !detail.isManualConsignee
                          ? 'text-gray-600 bg-gray-100 border-gray-300 cursor-pointer'
                          : 'text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                      }`}
                      title={
                        detail.isManualConsignee
                          ? "CIF deshabilitado - No aplica para consignatarios manuales"
                          : detail.ff
                            ? "CIF (Cost, Insurance, and Freight) - Activo porque hay FF asignado"
                            : "CIF deshabilitado - Requiere FF (Freight Forwarder) asignado"
                      }
                    />
                  </td>

                  {/* Acciones */}
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    {data.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onRemoveDetail(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Eliminar detalle"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>

                {/* Fila expandible con textarea para consignatarios manuales */}
                <tr className="bg-gray-100">
                  <td colSpan="8" className="border border-gray-300 px-4 py-3">
                    <div>
                      {/* Textarea para additional_consignees */}
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Consignatarios que NO se notificarán por correo: {detail.isManualConsignee && <span className="text-red-500">*</span>}
                      </label>
                      <textarea
                        value={detail.additional_consignees || ''}
                        onChange={(e) => onChange('additional_consignees', e.target.value, index)}
                        disabled={!detail.isManualConsignee && !detail.isTransportistaConsignee}
                        rows="3"
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                          (detail.isManualConsignee || detail.isTransportistaConsignee)
                            ? 'border-indigo-300 focus:ring-indigo-500 bg-white'
                            : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                        placeholder={(detail.isManualConsignee || detail.isTransportistaConsignee)
                          ? "Este espacio corresponde solo a texto de referencia para llevar un registro interno. Digite máximo 3 consignatarios de forma manual, presione ENTER para separarlos en líneas diferentes. Si desea también puede separarlos por coma, guión u otro separador. Estos consignatarios no serán notificados por correo electrónico."
                          : "Para activar este espacio, 1. Seleccione 'Digitar manualmente' en la lista de consignatarios (aplica para consignatarios que no están registrados en el sistema como usuarios y por lo tanto, no serán notificados por su FF) 2. Seleccione un FF (Transportista) de la lista de consignatarios (aplica para cuando un usuario del sistema tipo Transportista, se trasnforma en un consignatario en un BL y se desea que su cliente/s no sean notificado/s por correo)"
                        }
                      />
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mensaje de error general */}
      {errors.details && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{errors.details}</p>
        </div>
      )}

      {/* Información adicional */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          <strong>Nota:</strong> Los campos marcados con * son obligatorios. 
          Debe completar al menos una línea de detalle completa para continuar.
          No se permite repetir un mismo número de BL.
          No se permite repetir un mismo número de factura comercial para un BL.
          Todo BL siempre incluirá la lista de usuarios notificados por defecto.
          Todo BL siempre incluirá el usuario actual que registra como parte de sus notificados.
          Si selecciona un Vendedor en un BL, recibirá email. 
          Todos los proveedores que tengan correo asignado y su(s) notificado(s) recibirán email.
          <strong>Combinaciones permitidas:</strong> 
          1. Cliente directo: Seleccione de la lista un consignatario con rol Cliente, seleccione uno o varios proveedores, opcionalmente seleccione un vendedor. Solo el cliente y sus notificados reciben email.
          2. FF directo: Seleccione de la lista un consignatario con rol Cliente, seleccione uno o varios proveedores, seleccione un Transportista de la lista de FF, opcionalmente seleccione un vendedor. Solo el FF y sus notificados reciben email.
          3. FF con CIF: Seleccione de la lista un consignatario con rol Cliente, seleccione uno o varios proveedores, seleccione un Transportista de la lista de FF, opcionalmente seleccione un vendedor. Marque CIF. El FF y sus notificados, el cliente y sus notificados reciben email.
          4. FF con clientes temporales: Seleccione de la lista "Digitar consignatarios manualmente" y llene obligatoriamente el espacio abajo con el texto necesario, seleccione uno o varios proveedores, seleccione obligatoriamente un Transportista de la lista de FF, opcionalmente seleccione un vendedor. Solo el FF y sus notificados reciben email.
          5. FF como consignatario: Seleccione de la lista un consignatario con rol Transportista, llene obligatoriamente el espacio abajo con texto de consignatarios temporales, seleccione uno o varios proveedores, seleccione un Transportista de la lista de FF, opcionalmente seleccione un vendedor. Solo el FF y sus notificados reciben email.
          6. FF como consignatario con CIF: Seleccione de la lista un consignatario con rol Transportista, llene obligatoriamente el espacio abajo con texto de consignatarios temporales, seleccione uno o varios proveedores, seleccione un Transportista de la lista de FF, opcionalmente seleccione un vendedor. Marque CIF. El FF y sus notificados, el cliente y sus notificados reciben email.
        </p>
      </div>
    </div>
  );
};

export default DetailsSection;